<template>
  <b-container class="my-4">
    <h2 class="mb-1">Generar golden tickets</h2>
    <p class="text-muted">
      Códigos al portador para la campaña de Instagram. Cada uno sirve
      <strong>una sola vez</strong>, para quien lo canjee primero.
    </p>

    <b-card>
      <b-row>
        <b-col md="3" class="mb-3">
          <label>Cantidad</label>
          <b-form-input v-model.number="form.cantidad" type="number" min="1" :max="MAX" />
          <small class="text-muted">
            Conviene de a 20–25: con lotes grandes y vigencia corta, la mayoría
            vence sin repartirse.
          </small>
        </b-col>
        <b-col md="3" class="mb-3">
          <label>Usos por ticket</label>
          <b-form-input v-model.number="form.usos" type="number" min="1" />
        </b-col>
        <b-col md="6" class="mb-3">
          <label>Brochure</label>
          <b-form-select v-model="brochure" @change="aplicarBrochure">
            <b-form-select-option
              v-for="b in BROCHURES"
              :key="b.palabra"
              :value="b.palabra"
              >{{ b.nombre }} — comenta «{{ b.palabra }}»</b-form-select-option
            >
            <b-form-select-option :value="OTRA"
              >Otra campaña…</b-form-select-option
            >
          </b-form-select>
          <small class="text-muted" v-if="brochureElegido">
            Pruebas que anuncia: {{ brochureElegido.tests }}.
          </small>
          <small class="text-muted" v-else>
            Para una campaña nueva, que todavía no tiene plantilla de PDF.
          </small>
        </b-col>

        <!-- Solo para campañas nuevas: con un brochure elegido, la palabra y el
             servicio salen de él y no se escriben a mano. Una palabra mal
             tipeada genera un lote entero que le pide a la gente comentar algo
             que no existe. -->
        <template v-if="!brochureElegido">
          <b-col md="3" class="mb-3">
            <label>Palabra del post</label>
            <b-form-input v-model="form.palabra" placeholder="PALABRA" />
          </b-col>
          <b-col md="3" class="mb-3">
            <label>Servicio</label>
            <b-form-select v-model="form.campo">
              <b-form-select-option
                v-for="s in servicios"
                :key="s.field"
                :value="s.field"
                >{{ s.label }}</b-form-select-option
              >
            </b-form-select>
          </b-col>
        </template>
        <b-col md="8" class="mb-3">
          <label>Enlace de la publicación</label>
          <b-form-input
            v-model="form.post_url"
            placeholder="https://www.instagram.com/p/..."
          />
          <small class="text-muted">
            A donde se manda a quien llegue con un ticket ajeno. Va por lote:
            el PDF de esta campaña sigue apuntando a esta publicación cuando
            salga la siguiente.
          </small>
        </b-col>
        <b-col md="12" class="mb-3">
          <b-form-checkbox v-model="form.personal">
            Ticket personal (para quien comentó en Instagram)
          </b-form-checkbox>
          <small class="text-muted">
            Márcalo cuando el lote sea de tickets que se mandan por mensaje
            directo, uno a cada persona que comentó. Sin marcar son los del
            lote que se reparte a ciegas y se reenvía solo. Es lo que después
            permite contar cuántos mensajes directos terminaron en un canje.
          </small>
        </b-col>
        <b-col md="4" class="mb-3">
          <label>Vence el (opcional)</label>
          <b-form-input v-model="form.expiration_date" type="date" />
          <small class="text-muted">
            Sin fecha no vencen nunca. La urgencia es lo que empuja a canjear el
            mismo día.
          </small>
        </b-col>
      </b-row>

      <b-button variant="primary" :disabled="generando || !valido" @click="generar">
        {{ generando ? "Generando…" : "Generar tickets" }}
      </b-button>

      <b-alert :show="!!error" variant="danger" class="mt-3">{{ error }}</b-alert>
    </b-card>

    <b-card v-if="codigos.length" class="mt-4">
      <h5>
        {{ codigos.length }} tickets de {{ resumen.usos }} usos
        <span v-if="resumen.palabra">· {{ resumen.palabra }}</span>
      </h5>
      <p class="text-muted mb-3">
        <strong>Descarga los PDFs</strong>: son los que mandas por mensaje
        directo, uno por ticket. Y guarda el CSV, que es el registro de a quién
        le mandaste cada uno — sin él, mandar el mismo PDF dos veces deja a
        alguien con un código quemado. Los códigos no se vuelven a mostrar.
      </p>

      <b-button
        size="sm"
        variant="primary"
        class="mr-2"
        :disabled="bajandoPdfs"
        @click="bajarPdfs"
      >
        {{ bajandoPdfs ? "Generando PDFs…" : "Descargar PDFs" }}
      </b-button>
      <b-button size="sm" variant="outline-primary" class="mr-2" @click="bajarCsv">
        Descargar CSV
      </b-button>
      <b-button size="sm" variant="outline-secondary" @click="copiar">
        {{ copiado ? "Copiado" : "Copiar códigos" }}
      </b-button>

      <b-table
        class="mt-3"
        small
        striped
        :items="filas"
        :fields="['codigo', 'enlace']"
      />
    </b-card>
  </b-container>
</template>

<script>
import ticketService from "../../../services/ticketService";

// Los tres brochures que existen hoy, con la palabra que se comenta en cada
// publicación. Los tres reparten el MISMO servicio —corrección de tests—; lo
// que cambia es qué pruebas anuncia el PDF y, por lo tanto, qué plantilla se
// usa y qué palabra hay que comentar.
//
// Es una lista y no un campo de texto porque la palabra viaja impresa en el
// PDF y se la muestra a quien llega con un ticket ajeno: escribirla mal genera
// un lote entero que le pide a la gente comentar algo que no existe, y no hay
// forma de notarlo hasta que nadie comente.
const BROCHURES = [
  {
    palabra: "FONO",
    nombre: "Fonoaudiología",
    tests: "TEPROSIF-R, STSG, IDTEL y TECAL",
    campo: "tests_uses",
  },
  {
    palabra: "WISC",
    nombre: "Psicología",
    tests: "WISC-V, WAIS-IV e ICAP",
    campo: "tests_uses",
  },
  {
    palabra: "SENSORIAL",
    nombre: "Terapia ocupacional",
    tests: "Perfil Sensorial 2",
    campo: "tests_uses",
  },
];

// El valor de "Otra campaña…": deja escribir la palabra y elegir el servicio a
// mano, para cuando haya un brochure nuevo antes de que esta lista lo sepa.
const OTRA = "__otra__";

// Los mismos campos de uso que entiende el canje.
const SERVICIOS = [
  { field: "tests_uses", label: "Corrección de Tests" },
  { field: "planifications_uses", label: "Planificaciones" },
  { field: "materials_uses", label: "Materiales" },
  { field: "documents_uses", label: "Documentos" },
  { field: "eureka_uses", label: "Eureka" },
];

// El tope del backend (`MAX_TICKETS_POR_LOTE`). Se repite acá solo para avisar
// antes de pedir; quien manda es el backend.
const MAX = 200;

const BASE = "https://www.utopie.cl/c/";

export default {
  name: "GoldenTicketsAdmin",
  data() {
    return {
      MAX,
      OTRA,
      BROCHURES,
      servicios: SERVICIOS,
      brochure: BROCHURES[0].palabra,
      generando: false,
      bajandoPdfs: false,
      copiado: false,
      error: "",
      codigos: [],
      resumen: { usos: 0, palabra: "" },
      form: {
        cantidad: 25,
        usos: 10,
        campo: BROCHURES[0].campo,
        palabra: BROCHURES[0].palabra,
        post_url: "",
        // Por defecto NO: el lote normal es el que se reparte a ciegas, y una
        // casilla marcada de fábrica convierte todo en personal sin que nadie
        // lo decida.
        personal: false,
        expiration_date: "",
      },
    };
  },
  computed: {
    brochureElegido() {
      return BROCHURES.find((b) => b.palabra === this.brochure) || null;
    },
    valido() {
      return (
        this.form.cantidad >= 1 &&
        this.form.cantidad <= MAX &&
        this.form.usos >= 1 &&
        !!this.form.palabra
      );
    },
    filas() {
      return this.codigos.map((c) => ({ codigo: c, enlace: BASE + c }));
    },
  },
  methods: {
    // Elegir brochure fija la palabra y el servicio; "Otra campaña…" los
    // vacía para que se escriban a mano.
    aplicarBrochure() {
      const b = this.brochureElegido;
      this.form.palabra = b ? b.palabra : "";
      this.form.campo = b ? b.campo : "tests_uses";
    },
    generar() {
      this.generando = true;
      this.error = "";
      ticketService.createBatch(this.form).then(
        (data) => {
          this.generando = false;
          this.codigos = data.codigos || [];
          this.resumen = { usos: data.usos, palabra: data.palabra };
        },
        (err) => {
          this.generando = false;
          this.error =
            (err.response && err.response.data && err.response.data.message) ||
            "No se pudieron generar los tickets.";
        }
      );
    },
    csv() {
      // Las columnas que necesita el registro: las tres últimas se llenan a
      // mano al repartir, y son las que evitan mandar el mismo ticket dos veces.
      const cab = "codigo,palabra,usos,vence,enlace,enviado_a,fecha_envio";
      const filas = this.codigos.map((c) =>
        [
          c,
          this.resumen.palabra,
          this.resumen.usos,
          this.form.expiration_date,
          BASE + c,
          "",
          "",
        ].join(",")
      );
      return [cab].concat(filas).join("\n");
    },
    // El ZIP lo arma el servidor: un PDF por ticket, con su QR ya estampado.
    bajarPdfs() {
      this.bajandoPdfs = true;
      this.error = "";
      ticketService.pdfs(this.codigos).then(
        (blob) => {
          this.bajandoPdfs = false;
          this.descargar(
            blob,
            `tickets_${this.resumen.palabra || "lote"}_${this.hoy()}.zip`
          );
        },
        (err) => {
          this.bajandoPdfs = false;
          // El error del servidor viaja DENTRO del blob, porque la petición
          // pidió binario. Sin leerlo, cualquier falla mostraba el mismo
          // mensaje inventado y mandaba a buscar el problema donde no estaba.
          const cuerpo = err.response && err.response.data;
          if (cuerpo && typeof cuerpo.text === "function") {
            cuerpo.text().then(
              (txt) => {
                this.error = this.mensajeDe(txt);
              },
              () => {
                this.error = "No se pudieron generar los PDFs.";
              }
            );
          } else {
            this.error = "No se pudieron generar los PDFs.";
          }
        }
      );
    },
    mensajeDe(txt) {
      try {
        return JSON.parse(txt).message || "No se pudieron generar los PDFs.";
      } catch (e) {
        return "No se pudieron generar los PDFs.";
      }
    },
    hoy() {
      return new Date().toISOString().slice(0, 10);
    },
    descargar(blob, nombre) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nombre;
      a.click();
      URL.revokeObjectURL(url);
    },
    bajarCsv() {
      this.descargar(
        new Blob([this.csv()], { type: "text/csv;charset=utf-8" }),
        `tickets_${this.resumen.palabra || "lote"}_${this.hoy()}.csv`
      );
    },
    copiar() {
      const texto = this.codigos.join("\n");
      // `clipboard` no existe fuera de https (ni en localhost sin permiso): si
      // falla, el CSV sigue estando y la tabla se puede seleccionar a mano.
      const ok = () => {
        this.copiado = true;
        setTimeout(() => (this.copiado = false), 2000);
      };
      if (navigator.clipboard) navigator.clipboard.writeText(texto).then(ok, () => {});
      else ok();
    },
  },
};
</script>
